'use client'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { SearchInput } from '@/ui/atoms/search-input/SearchInput'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { cn } from '@/ui/utils/style'
import { normalizeForSearch } from '@/utils/strings'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'

export interface TokenFilterDialogProps {
  options: string[]
  selected: string[]
  onSelect: (symbols: string[]) => void
  title: string
  tokenAddressBySymbol?: Record<string, string>
}

export function TokenFilterDialog({
  options,
  selected,
  onSelect,
  title,
  tokenAddressBySymbol,
}: TokenFilterDialogProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredOptions = options.filter((symbol) => {
    const normalizedSymbol = normalizeForSearch(symbol)
    const normalizedQuery = normalizeForSearch(searchQuery)
    return normalizedSymbol.includes(normalizedQuery)
  })

  const handleToggle = (symbol: string) => {
    if (selected.includes(symbol)) {
      onSelect(selected.filter((s) => s !== symbol))
    } else {
      onSelect([...selected, symbol])
    }
  }

  const handleClearAll = () => {
    onSelect([])
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      setSearchQuery('')
    }
  }

  const displayText =
    selected.length === 0 ? title : selected.length === 1 ? selected[0] : `${selected.length} selected`

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-8 items-center gap-1.5 rounded-full px-3 font-medium text-sm transition-colors',
            selected.length > 0
              ? 'bg-primary text-primary-foreground'
              : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
          )}
        >
          {displayText}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent className="w-72 p-4 sm:w-80" aria-describedby={undefined} showCloseButton={false}>
        <div className="mb-3 flex items-center justify-between">
          <DialogTitle className="font-semibold text-base">Select {title}</DialogTitle>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-muted-foreground text-xs hover:text-foreground"
            >
              Clear all
            </button>
          )}
        </div>
        <SearchInput
          value={searchQuery}
          placeholder={`Search ${title.toLowerCase()}...`}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />
        <div className="mt-3 max-h-64 overflow-y-auto">
          <div className="flex flex-col gap-1">
            {filteredOptions.map((symbol) => (
              <button
                key={symbol}
                type="button"
                onClick={() => handleToggle(symbol)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors',
                  selected.includes(symbol)
                    ? 'bg-primary/20 text-primary'
                    : 'text-muted-foreground hover:bg-white/5 hover:text-foreground',
                )}
              >
                <TokenIcon
                  token={{ symbol: TokenSymbol(symbol), address: tokenAddressBySymbol?.[symbol] }}
                  className="h-5 w-5"
                />
                {symbol}
                {selected.includes(symbol) && <span className="ml-auto text-primary">✓</span>}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="py-4 text-center text-muted-foreground text-sm">No tokens found</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
